import { useState, useMemo, useEffect } from 'react';
import { Product } from '@repo/types';
import { useNextProductAvailability } from '@repo/widget-utils/services/hooks/product';
import { TZDate, tzdate } from '@repo/tzdate';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';

export default function useAvailabilitySearchPeriod(
    product: Product | null,
    selectedOptionId?: string,
) {
    const configurations = useConfigurations();
    const { data: nextProductAvailability } = useNextProductAvailability(
        product?.id,
        configurations.includeClosedAvailability,
        selectedOptionId,
    );

    const nextProductAvailabilityStart = useMemo(() => {
        return nextProductAvailability ? tzdate(nextProductAvailability.next, false) : undefined;
    }, [nextProductAvailability]);

    const [availabilitySearchPeriod, setAvailabilitySearchPeriod] = useState<{
        startDay: TZDate | null;
        endDay: TZDate | null;
    }>(() => {
        return { startDay: null, endDay: null };
    });

    useEffect(() => {
        let startDay: TZDate;
        let endDay: TZDate;

        if (configurations.openingDate) {
            const openingDate = tzdate(configurations.openingDate);
            const now = TZDate.now();

            startDay =
                nextProductAvailabilityStart && openingDate.isBefore(nextProductAvailabilityStart)
                    ? nextProductAvailabilityStart.startOf('month')
                    : openingDate.isBefore(now)
                      ? now.startOf('month')
                      : openingDate;

            endDay = startDay.endOf('month').add(1, 'day').startOf('day');
        } else if (nextProductAvailabilityStart) {
            startDay = nextProductAvailabilityStart.startOf('month').startOf('day');
            endDay = nextProductAvailabilityStart.endOf('month').add(1, 'day').startOf('day');
        } else {
            const now = TZDate.now();
            startDay = now.startOf('month');
            endDay = startDay.endOf('month').add(1, 'day').startOf('day');
        }

        setAvailabilitySearchPeriod({ startDay, endDay });
    }, [configurations.openingDate, nextProductAvailabilityStart]);

    return {
        nextProductAvailabilityStart,
        availabilitySearchPeriod,
        setAvailabilitySearchPeriod,
    };
}
